import { useCallback } from 'react';
import { dispatcher } from 'dispatcher';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';
import { useLocation } from 'react-router-dom';
import { useTranslations } from 'js/hooks/i18n';
import { openNewWindow } from 'js/utils/url.util';
import { useDeviceSelector } from 'js/store/hooks';
import { userSetAttributesAction } from 'js/hooks/user/store/actions';
import { useSmartClosePopups } from 'js/components/common/PopupManager';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { showCommonPopupAction } from 'js/components/common/popups/CommonPopup';
import { useNavigateToPrevPage } from 'js/components/system/Router/components/PageRouter/hooks';

import * as Types from '../types';
import * as helpers from '../helpers';
import { GAME_LAUNCH_METHODS } from '../constants';
import { useGetGameConfig } from './useGetGameConfig';
import { useGetLaunchMethod } from './useGetLaunchMethod';
import { useLaunchByRedirect } from './useLaunchByRedirect';
import { useLaunchInNewWindow } from './useLaunchInNewWindow';
import { useGetWrapperLaunchUrl } from './useGetWrapperLaunchUrl';
import { useGetRedirectLaunchUrl } from './useGetRedirectLaunchUrl';

export const useOpenGame = () => {
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const closePopups = useSmartClosePopups();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const translate = useTranslations('playtech.mobile.system.launcher.gameUnavailablePopup.');
    const navigateToPrevPage = useNavigateToPrevPage();
    const getGameConfig = useGetGameConfig();
    const getWrapperLaunchUrl = useGetWrapperLaunchUrl();
    const getRedirectLaunchUrl = useGetRedirectLaunchUrl();
    const getLaunchMethod = useGetLaunchMethod();
    const launchByRedirect = useLaunchByRedirect();
    const launchInNewWindow = useLaunchInNewWindow();

    return useCallback(({ gameCode, mode, additionalQueryString, ...options }: Types.ILaunchGameData): void => {
        const gameConfig = getGameConfig(gameCode);
        const launchMethod = getLaunchMethod({ gameCode });
        const gameWindow = launchMethod === GAME_LAUNCH_METHODS.REDIRECT_IN_NEW_WINDOW
            ? openNewWindow() : null;
        const isLaunchInWrapper = launchMethod && [
            GAME_LAUNCH_METHODS.WRAPPER_IN_NEW_WINDOW,
            GAME_LAUNCH_METHODS.WRAPPER_IN_THIS_WINDOW
        ].includes(launchMethod);

        if (!gameConfig) {
            return;
        }

        closePopups('gameLaunch');
        dispatcher.dispatch('game:info:close', gameCode);
        dispatch(userSetAttributesAction({
            launchedGame: {
                code: gameCode,
                mode,
                queryString: additionalQueryString,
                ...options
            }
        }));

        (async () => {
            try {
                const launcherUrl = isLaunchInWrapper
                    ? getWrapperLaunchUrl({ gameCode, mode, additionalQueryString })
                    : await getRedirectLaunchUrl({ gameCode, mode, additionalQueryString });

                if (!launcherUrl) {
                    return;
                }

                switch (launchMethod) {
                    case GAME_LAUNCH_METHODS.REDIRECT_IN_NEW_WINDOW:
                    case GAME_LAUNCH_METHODS.WRAPPER_IN_NEW_WINDOW: {
                        if (helpers.isGamingPath(pathname)) {
                            navigateToPrevPage();
                        }

                        launchInNewWindow({ launcherUrl, gameWindow });

                        break;
                    }
                    case GAME_LAUNCH_METHODS.REDIRECT_IN_THIS_WINDOW:
                    case GAME_LAUNCH_METHODS.WRAPPER_IN_THIS_WINDOW:
                    default:
                        launchByRedirect({ launcherUrl });
                        break;
                }

                options.onGameLaunched?.();
                // for game launch tracking
                publicEventsAPIService.trigger('game-launch', {
                    mode,
                    options,
                    game: gameConfig,
                    queryString: additionalQueryString
                });
            } catch {
                gameWindow?.close();

                dispatch(showCommonPopupAction({
                    title: `${gameConfig.name} ${translate('title')}`,
                    content: translate(isDesktop ? 'desktop-content' : 'mobile-content')
                }));
            }
        })();
    }, [
        dispatch, getRedirectLaunchUrl, translate, getLaunchMethod, getGameConfig,
        pathname, closePopups, isDesktop, launchByRedirect, launchInNewWindow,
        getWrapperLaunchUrl, navigateToPrevPage
    ]);
};
